import { requestContext } from 'client/utils/request-context/request-context';
import { FLAVOR_GROUPS } from 'client/site-modules/shared/constants/flavors';

/**
 * @typedef {Object.<string, boolean>} FlavorData - Flavor tokens as object keys with true values, e.g. { ctrl: true, tg_dealer123: true }
 */

/**
 * Flavor token storage from URL params/cookies.
 * Business logic in flavor-targeting.js.
 */
export const flavorStore = {
  /**
   * Returns all flavor tokens.
   * @returns {FlavorData|null|undefined}
   */
  findAll() {
    return requestContext.get('flavor');
  },

  /**
   * Checks if token exists.
   * @param {string} tokenName
   * @returns {boolean}
   */
  has(tokenName) {
    const data = this.findAll();
    if (!data) return false;

    return data[tokenName] ?? false;
  },

  /**
   * Checks if group has any tokens.
   * @param {string} groupName
   * @returns {boolean}
   */
  hasGroup(groupName) {
    const data = this.findAll();
    if (!data) return false;

    const groupPattern = FLAVOR_GROUPS[groupName];
    if (!groupPattern) return false;

    return Object.keys(data).some(token => groupPattern.test(token));
  },

  /**
   * Returns comma-separated token values matching the given group, or null if none found.
   * @param {string} groupName
   * @returns {string|null}
   */
  findByGroup(groupName) {
    if (!this.hasGroup(groupName)) return null;

    const pattern = FLAVOR_GROUPS[groupName];
    return Object.keys(this.findAll())
      .filter(token => pattern.test(token))
      .join(',');
  },

  /**
   * Stores flavor data in requestContext.
   * @param {FlavorData|null} flavorData
   */
  set(flavorData) {
    requestContext.set('flavor', flavorData ?? null, { sendToClient: true });
  },
};
